import { useEffect, useState, useCallback } from 'react';
import { logError } from '../errors/error-reporting.js';
import 'urql';
import '@tanstack/react-query';
import 'lodash-es';

/**
 * Just like useState but gets/sets the value in the browser's local storage.
 * 'key' should be a constant string. 'initialValue' is returned when local
 * storage does not have any data yet.
 */ function useLocalStorage(key, initialValue) {
    const { value, setValueAndStore } = useStorage(window.localStorage, key, initialValue);
    return [
        value,
        setValueAndStore
    ];
}
/**
 * Just like useState, but gets/sets the value in the browser's session storage.
 * 'key' should be a constant string. 'initialValue' is returned when session
 * storage does not have any data yet.
 */ function useSessionStorage(key, initialValue) {
    const { value, setValueAndStore } = useStorage(window.sessionStorage, key, initialValue);
    return [
        value,
        setValueAndStore
    ];
}
/**
 * Just like useLocalStorage, but syncs values across browser tabs.
 */ function useLocalStorageSynced(key, initialValue) {
    const { value, setValue, setValueAndStore } = useStorage(window.localStorage, key, initialValue);
    useEffect(()=>{
        // When local storage changes due to some other tab updating it, just
        // update our local state value
        const sync = (e)=>{
            if (e.key !== key || e.newValue === null) {
                return;
            }
            setValue(JSON.parse(e.newValue));
        };
        window.addEventListener('storage', sync);
        return ()=>{
            window.removeEventListener('storage', sync);
        };
    }, [
        key,
        setValue
    ]);
    return [
        value,
        setValueAndStore
    ];
}
// Common functionality used by all storage hooks
function useStorage(storage, key, initialValue) {
    const { getItem, setItem } = accessStorage(storage, key);
    // Use state so that changes cause the page to re-render
    const [value, setValue] = useState(()=>{
        const storedValue = getItem();
        if (storedValue !== undefined) return storedValue;
        // Either the value isn't in storage yet or JSON parsing failed, so
        // set to the initial value in both places
        setItem(initialValue);
        return initialValue;
    });
    // Set in both places
    const setValueAndStore = useCallback((val)=>{
        setValue(val);
        trySetStorageItem(storage, key, val);
    }, [
        setValue,
        storage,
        key
    ]);
    return {
        value,
        setValue,
        setValueAndStore
    };
}
/**
 * @description
 * Hooks trigger re-renders, so use this function when you're solely
 * concerned with persisting state to storage and triggering a
 * re-render is a separate concern, for example handled by a containing
 * hook.
 */ const accessStorage = (storage, key)=>{
    const getItem = ()=>{
        try {
            const jsonString = storage.getItem(key);
            // When undefined is stored to storage via JSON.parse, the returned
            // value is a string version of undefined
            if (jsonString !== null && jsonString !== 'undefined') {
                return JSON.parse(jsonString);
            }
        } catch (e) {
            console.error(e);
        }
    };
    const setItem = (value)=>{
        trySetStorageItem(storage, key, value);
    };
    return {
        getItem,
        setItem
    };
};
/**
 * Wrapper around storage.setItem with a try/catch to avoid breaking the page
 * when something goes wrong with storage (e.g. hitting quota limits).
 */ const trySetStorageItem = (storage, key, value)=>{
    try {
        storage.setItem(key, JSON.stringify(value));
    } catch (e) {
        // Catch and log if something goes wrong because we do not want to break
        // the page when something goes wrong with storage.
        logError(e);
    }
};

export { accessStorage, trySetStorageItem, useLocalStorage, useLocalStorageSynced, useSessionStorage };
//# sourceMappingURL=browser-storage.js.map
